// A select-like input for picking a brand set.

import { useJobillaOrgId } from '@/hooks'
import ctl from '@netlify/classnames-template-literals'
import { createStore } from 'jotai'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SelectField } from '../../../rapid-ui/inputs/select-field'
import { logger } from '../../../utilities'
import { ConformedBranding } from '../../../views'
import { BrandLibraryPicker } from './brand-library'
import { BrandLibraryStoreProvider, useBrandLibraryStore } from './brand-library-store'
import { extractBrandSetName } from './extract-brand-set-name'

// start with a "small" version that assumes the default is good enough. In the future
// we can add a "large" version that looks more like a form field.

export type BrandSelectorProps<T extends string> = {
	items: Array<ConformedBranding>
	value: string
	onChange: (value: string) => void
	brandStore?: ReturnType<typeof createStore>
	campaignId?: string
}

export function BrandSelector<T extends string>({
	items,
	value,
	onChange,
	brandStore,
	campaignId,
}: BrandSelectorProps<T>) {
	const { t } = useTranslation('common')
	const [brandLibraryOpen, setBrandLibraryOpen] = useState(false)
	const store = useBrandLibraryStore({ overrideStore: brandStore })
	const jobillaOrgId = useJobillaOrgId()

	const selected = useMemo(() => items.find((b) => b.id === value), [items, value])

	// if no value at all, default to the bottom of the list
	useEffect(() => {
		if (!value && items.length > 0) {
			onChange(items[items.length - 1].id)
		}
	}, [value, items, onChange])

	const getDisplayName = (branding: ConformedBranding | undefined) => {
		if (!branding) { return t('branding.selectBrand') }
		return extractBrandSetName(branding.name, branding.website ?? '')
	}

	const renderPalette = (branding: ConformedBranding | undefined) => {
		if (!branding) { return null }
		const colors = [
			branding.background,
			branding.foreground,
			branding.accentBackground,
			branding.accentForeground,
		]
		return (
			<div className="flex items-center gap-1">
				{colors.map((c, i) => (
					<span
						key={`${branding?.id ?? 'x'}-c-${i}`}
						className="h-3 w-3 rounded-sm ring ring-grey-100/60"
						style={{ backgroundColor: c }}
					/>
				))}
			</div>
		)
	}

	return (
		<div className="w-full">
			<SelectField<ConformedBranding | null>
				options={items}
				value={selected ?? null}
				onChange={(b) => {
					if (b?.id !== selected?.id && b?.id) {
						onChange(b.id)
					}
				}}
				placeholder={t('branding.selectBrand')}
				renderOption={(option) => (
					option
					&& (
						<div className="flex items-center justify-between gap-4">
							<div className="flex items-center gap-3 min-w-0">
								<span
									className="h-6 w-6 shrink-0 bg-center bg-contain bg-no-repeat rounded"
									style={{ backgroundImage: `url('${option.logo?.url}')` }}
								/>
								<span className="truncate">{getDisplayName(option)}</span>
							</div>
							{renderPalette(option)}
						</div>
					)
				)}
				bottomSlot={
					<button
						className={ctl(`
							w-full cursor-pointer
							text-sm rounded px-1 mt-1 pt-3 pb-2
							bg-white border-t border-grey-100
							text-purple-800
							grid items-stretch justify-stretch gap-3
						`)}
						onMouseDown={(e) => e.preventDefault()}
						onClick={(e) => {
							e.stopPropagation()

							logger.track('Editor: Opened brand library', {
								data: {
									campaign_id: campaignId,
									organisation_id: jobillaOrgId,
									new_campaign: !campaignId,
								},
							})

							setBrandLibraryOpen(true)
						}}
					>
						{t('branding.openBrandingLibrary')}
					</button>
				}
			/>
			<BrandLibraryStoreProvider store={store}>
				<BrandLibraryPicker
					open={brandLibraryOpen}
					onSelect={(branding) => {
						onChange(branding.id)
						setBrandLibraryOpen(false)
					}}
					onClose={() => {
						setBrandLibraryOpen(false)
					}}
					campaignId={campaignId}
				/>
			</BrandLibraryStoreProvider>
		</div>
	)
}
